import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';
import { useAssetTransferAvailability } from '@/entities/account';
import { useInternalTransferModals } from '@/features/internal-transfer';
import { useAllOperationMethods } from '@/widgets/account-operations';
import { useDepositWithdrawAssetsDictionary } from '@/shared/api';
import type { WalletAssetActionsVisibility, WalletAssetRow } from './types';

const useWalletsAssetActions = (accountNumber: string | undefined) => {
  const navigate = useNavigate();
  const { session } = useSessionStore();
  const isVerified = session?.additional?.isVerified || false;

  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { depositMethods, withdrawMethods } = useAllOperationMethods();
  const { openInternalTransferFormModal } = useInternalTransferModals();
  const { depositWithdrawAssetsData, depositWithdrawAssetsLoading } =
    useDepositWithdrawAssetsDictionary();
  const { getAccountTypeTransferAvailability } = useAssetTransferAvailability();

  const getActionsVisibility = useCallback(
    (row: WalletAssetRow): WalletAssetActionsVisibility => {
      const assetsAvailableForDeposit =
        depositWithdrawAssetsData?.value.deposit.all || [];
      const assetsAvailableForWithdraw =
        depositWithdrawAssetsData?.value.withdraw.all || [];

      return {
        deposit: assetsAvailableForDeposit.includes(row.code),
        withdraw:
          row.baseAmount > 0 && assetsAvailableForWithdraw.includes(row.code),
        exchange: row.amount > 0,
        transfer: getAccountTypeTransferAvailability('ewallet'),
      };
    },
    [depositWithdrawAssetsData, getAccountTypeTransferAvailability]
  );

  const onDeposit = useCallback(
    (row?: WalletAssetRow) => {
      depositMethods.openOperationMethodsModal(
        row ? { assetGroup: row.group, assetCode: row.code } : undefined
      );
    },
    [depositMethods]
  );

  const onWithdraw = useCallback(
    (row?: WalletAssetRow) => {
      withdrawMethods.openOperationMethodsModal(
        row ? { assetGroup: row.group, assetCode: row.code } : undefined
      );
    },
    [withdrawMethods]
  );

  const onExchange = useCallback(
    (row?: WalletAssetRow) => {
      if (!isVerified) {
        openVerificationInfoModal();
        return;
      }

      navigate('/exchange', row ? { state: { asset: row.code } } : undefined);
    },
    [isVerified, openVerificationInfoModal, navigate]
  );

  const onTransfer = useCallback(
    (row: WalletAssetRow) => {
      openInternalTransferFormModal({
        initialContext: {
          accountType: 'ewallet',
          accountNumber: accountNumber ?? '',
          assetCode: row.code,
          direction: 'from',
        },
      });
    },
    [openInternalTransferFormModal, accountNumber]
  );

  return {
    getActionsVisibility,
    isActionsLoading: depositWithdrawAssetsLoading,
    onDeposit,
    onWithdraw,
    onExchange,
    onTransfer,
  };
};

export { useWalletsAssetActions };
